import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import {
  withClickBlocker,
  WithClickBlockerProps
} from "components/BlockableClickContext";
import ScrollableMobileTabBar from "components/ScrollableMobileTabBar/ScrollableMobileTabBar";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import {
  setOrderBy,
  setSelectedTabPreRaceTable
} from "store/actions/preRaceScreeningPage";
import {
  PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY,
  preRacesTableTabs
} from "../../options";

import useStyles from "./styles";

interface IProps {
  upcomingRaceEntriesQuery: racehorse360.IUpcomingRaceEntryFilter;
}

const PreRaceTableTabs: React.FC<WithClickBlockerProps & IProps> = props => {
  const { handleBlockableClick, upcomingRaceEntriesQuery } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { useListUpcomingRaceEntries } = useRacehorse360Api();

  const { data: preRaceEntriesTotalResultsData } = useListUpcomingRaceEntries(
    PRE_RACE_ENTRIES_TOTAL_RESULTS_QUERY_KEY,
    {
      query: upcomingRaceEntriesQuery,
      pagingOptions: {
        maxResults: 1,
        includeSummary: true
      }
    },
    { enabled: Boolean(upcomingRaceEntriesQuery.facilityIds) }
  );

  const selectedTab = useAppSelector(
    state => state.preRaceScreeningPage.selectedTab
  );

  const activeTabIndex = Math.max(
    0,
    preRacesTableTabs.findIndex(tab => tab.value === selectedTab?.value)
  );

  const handleTabChange = useCallback(
    (tabIndex: number) => {
      if (tabIndex === activeTabIndex) return;

      handleBlockableClick(() => {
        const currentTab = preRacesTableTabs[tabIndex];

        dispatch(setSelectedTabPreRaceTable(currentTab));
        currentTab.forceOrderBy &&
          dispatch(setOrderBy(currentTab.forceOrderBy));

        window.history.pushState(null, EMPTY_STRING, currentTab.path);
        window.scrollTo(0, 0);
      });
    },
    [dispatch, activeTabIndex, handleBlockableClick]
  );

  const getTabsWithTotalResults = () => {
    const totalNumber =
      preRaceEntriesTotalResultsData?.pagingInfo?.totalResults;

    return preRacesTableTabs.map(tab => {
      const isTabActive = tab.value === selectedTab.value;
      const shouldShowTotalResults = [
        isTabActive,
        typeof totalNumber === "number"
      ].every(Boolean);

      if (shouldShowTotalResults) {
        return `${tab.name} (${totalNumber})`;
      }

      return tab.name;
    });
  };

  useEffect(() => {
    const tab = preRacesTableTabs.find(
      item => item.path === history.location.pathname
    );

    if (tab) {
      dispatch(setSelectedTabPreRaceTable(tab));
      tab.forceOrderBy && dispatch(setOrderBy(tab.forceOrderBy));
    }
  }, [history.location.pathname, dispatch]);

  return (
    <ScrollableMobileTabBar
      className={classes.tabBar}
      tabs={getTabsWithTotalResults()}
      activeIndex={activeTabIndex}
      onActiveTabChange={handleTabChange}
    />
  );
};

export default withClickBlocker(React.memo(PreRaceTableTabs));
